import { WHATSAPP_TEMPLATE_SUPPORTED_LANGUAGE_CODES } from "@multi-chat/shared";

export interface TemplateLanguageOption {
  code: string;
  label: string;
}

function toBcp47(code: string): string {
  return code.replace(/_/g, "-");
}

/** Meta WhatsApp Manager shows English language names, including short region tags. */
const META_ENGLISH_LANGUAGE_LABELS: Record<string, string> = {
  en: "English",
  en_US: "English",
  en_GB: "English (UK)",
  en_AE: "English (UAE)",
  en_AU: "English (AUS)",
  en_CA: "English (CAN)",
};

export function englishTemplateLanguageLabel(code: string): string {
  const mapped = META_ENGLISH_LANGUAGE_LABELS[code];
  if (mapped) return mapped;
  try {
    const displayNames = new Intl.DisplayNames(["en"], { type: "language" });
    const bcp47 = toBcp47(code);
    const localized = displayNames.of(bcp47);
    if (localized && localized !== bcp47) return localized;
  } catch {
    // fall through
  }
  return code;
}

export function buildTemplateLanguageOptions(
  displayLocale: string,
  options?: { englishOnly?: boolean; hideCodes?: boolean },
): TemplateLanguageOption[] {
  if (options?.englishOnly) {
    return WHATSAPP_TEMPLATE_SUPPORTED_LANGUAGE_CODES.map((code) => ({
      code,
      label: englishTemplateLanguageLabel(code),
    }));
  }

  let displayNames: Intl.DisplayNames | undefined;
  try {
    displayNames = new Intl.DisplayNames([displayLocale], { type: "language" });
  } catch {
    displayNames = undefined;
  }

  return WHATSAPP_TEMPLATE_SUPPORTED_LANGUAGE_CODES.map((code) => {
    const bcp47 = toBcp47(code);
    const localized = displayNames?.of(bcp47);
    const label =
      localized && localized !== bcp47
        ? options?.hideCodes
          ? localized
          : `${localized} (${code})`
        : code;
    return { code, label };
  });
}

export function filterLanguageOptions(
  options: readonly TemplateLanguageOption[],
  query: string,
): TemplateLanguageOption[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return [...options];
  return options.filter(
    (option) =>
      option.code.toLowerCase().includes(normalized) ||
      option.label.toLowerCase().includes(normalized),
  );
}
